import * as React from 'react';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Modal } from './Modal';
import { Note, Review } from '../types/items';
import { CareerCard } from '../types/careerCard';
import { IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import MDEditor from '@uiw/react-md-editor';

type DraftableItem = Note | Review | CareerCard;

interface DraftRecoveryDialogProps<T extends DraftableItem> {
  isOpen: boolean;
  onClose: () => void;
  currentVersion: T;
  draftVersion: T;
  lastSaved: Date;
  onRecover: () => void;
  onDiscard: () => void;
}

interface ComparisonSection {
  title: string;
  current: React.ReactNode;
  recovered: React.ReactNode;
}

// Type guards
function isCareerCard(item: DraftableItem): item is CareerCard {
  return 'card_data' in item;
}

function isReview(item: DraftableItem): item is Review {
  return 'review_text' in item;
}

function isNote(item: DraftableItem): item is Note {
  return 'type' in item && item.type === 'note';
}

export function DraftRecoveryDialog<T extends DraftableItem>({
  isOpen,
  onClose,
  currentVersion,
  draftVersion,
  lastSaved,
  onRecover,
  onDiscard,
}: DraftRecoveryDialogProps<T>) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['title', 'content']));

  const toggleSection = (section: string) => {
    const newSections = new Set(expandedSections);
    if (newSections.has(section)) {
      newSections.delete(section);
    } else {
      newSections.add(section);
    }
    setExpandedSections(newSections);
  };

  const renderComparisonSection = (section: ComparisonSection) => {
    const isExpanded = expandedSections.has(section.title.toLowerCase());
    
    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(section.title.toLowerCase())}
          className="w-full flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <span className="font-medium">{section.title}</span>
          {isExpanded ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
        </button>
        {isExpanded && (
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</div>
              <div className="text-sm">{section.current}</div>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <div className="text-xs text-blue-600 dark:text-blue-300 mb-1">Recovered</div>
              <div className="text-sm">{section.recovered}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTags = (tags: string[] | undefined, isRecovered: boolean) => (
    <div className="flex flex-wrap gap-1">
      {tags?.map((tag: string, i: number) => (
        <span 
          key={i} 
          className={`px-2 py-0.5 rounded-full text-xs ${
            isRecovered 
              ? 'bg-blue-200 dark:bg-blue-800' 
              : 'bg-gray-200 dark:bg-gray-700'
          }`}
        >
          {tag}
        </span>
      ))}
    </div>
  );

  const getComparisonSections = (): ComparisonSection[] => {
    if (isCareerCard(currentVersion) && isCareerCard(draftVersion)) {
      return [
        {
          title: 'Title',
          current: currentVersion.card_data.title,
          recovered: draftVersion.card_data.title,
        },
        {
          title: 'Impact Metrics',
          current: (
            <div>
              <div>Career Impact: {currentVersion.career_impact.value}</div>
              <div>Engagement: {currentVersion.engagement_level.value}</div>
            </div>
          ),
          recovered: (
            <div>
              <div>Career Impact: {draftVersion.career_impact.value}</div>
              <div>Engagement: {draftVersion.engagement_level.value}</div>
            </div>
          ),
        },
        {
          title: 'Tags',
          current: renderTags(currentVersion.card_data.custom_tags, false),
          recovered: renderTags(draftVersion.card_data.custom_tags, true),
        },
      ];
    }

    if (isReview(currentVersion) && isReview(draftVersion)) {
      return [
        {
          title: 'Title',
          current: currentVersion.title,
          recovered: draftVersion.title,
        },
        {
          title: 'Content',
          current: (
            <div className="max-h-60 overflow-y-auto">
              <MDEditor.Markdown source={currentVersion.review_text} />
            </div>
          ),
          recovered: (
            <div className="max-h-60 overflow-y-auto">
              <MDEditor.Markdown source={draftVersion.review_text} />
            </div>
          ),
        },
        {
          title: 'Tags',
          current: renderTags(currentVersion.custom_tags, false),
          recovered: renderTags(draftVersion.custom_tags, true),
        },
      ];
    }

    // Note type
    if (isNote(currentVersion) && isNote(draftVersion)) {
      return [
        {
          title: 'Title',
          current: currentVersion.title,
          recovered: draftVersion.title,
        },
        {
          title: 'Content',
          current: (
            <div className="max-h-60 overflow-y-auto">
              <MDEditor.Markdown source={currentVersion.content} />
            </div>
          ),
          recovered: (
            <div className="max-h-60 overflow-y-auto">
              <MDEditor.Markdown source={draftVersion.content} />
            </div>
          ),
        },
        {
          title: 'Impact Metrics',
          current: (
            <div>
              <div>Career Impact: {currentVersion.career_impact}</div>
              <div>Engagement: {currentVersion.engagement_level}</div>
            </div>
          ),
          recovered: (
            <div>
              <div>Career Impact: {draftVersion.career_impact}</div>
              <div>Engagement: {draftVersion.engagement_level}</div>
            </div>
          ),
        },
        {
          title: 'Tags',
          current: renderTags(currentVersion.custom_tags, false),
          recovered: renderTags(draftVersion.custom_tags, true),
        },
      ];
    }

    return [];
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-4 sm:p-6 w-full max-w-[95vw] md:max-w-5xl mx-auto">
        <h2 className="text-xl font-semibold mb-4">
          Recover Unsaved Changes?
        </h2>
        <p className="mb-4 text-gray-600 dark:text-gray-300">
          A more recent version of this {getItemType(currentVersion)} was found, saved{' '}
          {formatDistanceToNow(lastSaved, { addSuffix: true })}.
          Would you like to recover it?
        </p>

        <div className="mb-6 overflow-y-auto max-h-[70vh]">
          {getComparisonSections().map((section) => (
            <React.Fragment key={section.title.toLowerCase()}>
              {renderComparisonSection(section)}
            </React.Fragment>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => {
              onDiscard();
              onClose();
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Discard Draft
          </button>
          <button
            onClick={() => {
              onRecover();
              onClose();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Recover Draft
          </button>
        </div>
      </div>
    </Modal>
  );
}

function getItemType(item: DraftableItem): string {
  if (isCareerCard(item)) return 'card';
  if (isReview(item)) return 'review';
  return 'note';
} 